// File: ImageUploader.tsx
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { getCookies } from "cookies-next";
import { config } from '../../data/config';
import { useTranslation } from 'react-i18next';
import Swal from 'sweetalert2';

interface ImageItem {
  id: string;
  url: string;
}

interface ImageUploaderProps {
  id: string | number;
  component: string;
  maxFiles?: number;
  onImageChange?: (images: ImageItem[]) => void; // Optional callback
  previewOnly?: boolean; // If true, no upload/delete UI
}

export default function ImageUploader({
  id,
  component,
  maxFiles = 1,
  onImageChange,
  previewOnly = false
}: ImageUploaderProps) {
  const { t } = useTranslation();
  const cookie = getCookies();

  const [images, setImages] = useState<ImageItem[]>([]);
  const [uploading, setUploading] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  // Load images from backend
  const loadImages = async () => {
    try {
      const res = await axios.get(
        `${config.url}/v1/dashboard/filemanager`,
        {
          params: { id, component },
          headers: { Authorization: 'Bearer ' + cookie['token'] },
        }
      );
      const data = res.data.data || [];
      setImages(Array.isArray(data) ? data : []);
      onImageChange?.(data);
    } catch (error) {
      console.error('Failed to load images', error);
      setImages([]);
    }
  };

  // Handle file drop/select
  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Optional: enforce single image
    if (maxFiles === 1 && images.length > 0) {
      const result = await Swal.fire({
        title: t('تصویر قبلی جایگزین شود؟'),
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: t('بله'),
        cancelButtonText: t('خیر'),
      });
      if (!result.isConfirmed) return;
    }

    const formData = new FormData();
    formData.append("file", file);
    formData.append("component", component);
    formData.append("parent", String(id));

    setUploading(true);
    try {
      await axios.post(
        `${config.url}/v1/dashboard/filemanager/single`,
        formData,
        {
          headers: {
            Authorization: 'Bearer ' + cookie['token'],
            "Content-Type": "multipart/form-data"
          },
        }
      );
      await loadImages(); // Refresh
    } catch (error) {
      Swal.fire({
        title: t('خطا در آپلود'),
        icon: 'error',
        text: t('لطفاً دوباره تلاش کنید.')
      });
    } finally {
      setUploading(false);
      // Reset input
      e.target.value = '';
    }
  };

  // Delete image
  const handleDelete = async (imageId: string) => {
    const result = await Swal.fire({
      title: t('آیا مطمئن هستید؟'),
      text: t('این تصویر برای همیشه حذف خواهد شد.'),
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: t('بله، حذف شود'),
      cancelButtonText: t('لغو'),
    });

    if (!result.isConfirmed) return;

    setDeletingId(imageId);
    try {
      await axios.delete(
        `${config.url}/v1/dashboard/filemanager/${imageId}`,
        {
          headers: { Authorization: 'Bearer ' + cookie['token'] },
        }
      );
      await loadImages();
    } catch (error) {
      Swal.fire({
        title: t('Error deleting file'),
        icon: 'error'
      });
    } finally {
      setDeletingId(null);
    }
  };

  useEffect(() => {
    loadImages();
  }, [id, component]);

  // Render images
  const renderImages = () => (
    <div className="space-y-3">
      {images.map((item) => (
        <div key={item.id} className="text-center">
          <img
            src={`${config.fronturl}/${item.url}`}
            alt=""
            className="max-w-full h-auto rounded border mx-auto"
            onError={(e) => (e.currentTarget.src = '/placeholder-image.jpg')}
          />
          {!previewOnly && (
            <button
              onClick={() => handleDelete(item.id)}
              disabled={deletingId === item.id}
              className="mt-2 w-full h-10 bg-red-600 hover:bg-red-700 text-white font-medium rounded disabled:opacity-50"
            >
              {deletingId === item.id ? t('در حال حذف...') : t('حذف')}
            </button>
          )}
        </div>
      ))}
    </div>
  );

  return (
    <div className="border rounded-lg p-3" dir="rtl">
      {renderImages()}

      {/* Upload Button (only if not previewOnly and under limit) */}
      {!previewOnly && images.length < maxFiles && (
        <div className="mt-3">
          <label
            className={`block w-full h-12 rounded bg-gray-500 text-white flex items-center justify-center cursor-pointer
              hover:bg-gray-600 transition ${uploading ? 'opacity-70 cursor-not-allowed' : ''}`}
          >
            {uploading ? t('در حال آپلود...') : t('آپلود تصویر')}
            <input
              type="file"
              accept="image/*"
              onChange={handleFileChange}
              disabled={uploading}
              className="hidden"
            />
          </label>
        </div>
      )}

      {/* Empty state */}
      {!previewOnly && images.length === 0 && maxFiles > 0 && (
        <div className="text-center py-4 text-gray-500">
          {t('تصویری اضافه نشده است')}
        </div>
      )}
    </div>
  );
}

// Export a preview-only version if needed
export const ImagePreview = (props: Omit<ImageUploaderProps, 'previewOnly'>) => (
  <ImageUploader {...props} previewOnly={true} />
);